'use client'
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import CheckIcon from '@mui/icons-material/Check'
import MouseIcon from '@mui/icons-material/Mouse'
import BoltIcon from '@mui/icons-material/Bolt'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import SpeedIcon from '@mui/icons-material/Speed'
import StarIcon from '@mui/icons-material/Star'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import CloseIcon from '@mui/icons-material/Close'

import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'
import ConfigSlider from '@components/Form/ConfigSlider'
import SmartValueInput from '@/components/CustomInput/SmartValueInput'
import { Tooltip } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

const Mouse_Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    transition: opacity 0.2s ease;
`

// --- HERO SECTION (FULL WIDTH) ---

const Mouse_HeroSection = styled.div`
    position: relative;
    width: 100%;
    max-width: 860px;
    background: linear-gradient(135deg, #0a0a0f 0%, #161625 100%);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
    overflow: hidden;
    display: flex;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.5), transparent);
        z-index: 2;
    }
`

const Mouse_HeroContent = styled.div`
    position: relative;
    z-index: 5;
    display: flex;
    width: 100%;
    align-items: stretch;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`

const Mouse_GridPattern = styled.div`
    position: absolute;
    inset: 0;
    opacity: 0.15;
    background-image: 
        linear-gradient(rgba(99, 102, 241, 0.3) 1px, transparent 1px),
        linear-gradient(90deg, rgba(99, 102, 241, 0.3) 1px, transparent 1px);
    background-size: 30px 30px;
    mask-image: radial-gradient(circle at 30% 50%, black, transparent 80%);
    pointer-events: none;
    z-index: 1;
`

const Mouse_GlowEffect = styled.div`
    position: absolute;
    top: -50%;
    left: -20%;
    width: 80%;
    height: 200%;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%);
    z-index: 1;
    pointer-events: none;
`

const Mouse_EDPI_Display = styled.div`
    flex: 0 0 180px;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-right: 1px solid rgba(255, 255, 255, 0.05);
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.01) 0%, transparent 100%);

    @media (max-width: 768px) {
        border-right: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        padding: 14px;
        flex: auto;
    }
`

const Mouse_EDPI_Label = styled.div`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 3px;
    color: rgba(99, 102, 241, 0.8);
    font-weight: 700;
    margin-bottom: 5px;
    text-shadow: 0 0 10px rgba(99, 102, 241, 0.3);
`

const Mouse_EDPI_Value = styled.div`
    font-size: 2.8rem;
    font-weight: 800;
    line-height: 1;
    background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #6366f1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 0 20px rgba(99, 102, 241, 0.2));
    font-family: var(--font-roboto, sans-serif); 
`

const Mouse_Info_Side = styled.div`
    flex: 1;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;

    @media (max-width: 768px) {
        padding: 14px;
        align-items: center;
        text-align: center;
    }
`

const Mouse_InfoText = styled.p`
    margin: 0;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.5;
    max-width: 90%;
`

const Mouse_RecommendationBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 2px;
    background: rgba(0, 0, 0, 0.3);
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    align-self: flex-start;

    @media (max-width: 768px) {
        align-self: center;
    }

    .label {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: #6366f1;
        font-weight: 700;
    }

    .range {
        color: #fff;
        font-weight: 600;
        font-size: 0.9rem;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
    }
`

// --- MAIN LAYOUT ROW ---
const Mouse_LayoutRow = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;
    width: 100%;

    @media (max-width: 900px) {
        flex-direction: column;
    }
`

const Mouse_MainColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 25px;
    min-width: 0; // Prevent flex overflow
    margin-top: 20px;
`

const Mouse_SidebarColumn = styled.div`
    width: 400px;
    flex-shrink: 0;
`

// --- COMPACT CALCULATOR CARD ---

const Important_Label = styled.div`
    color: #f59e0b;
    font-size: 0.65rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    letter-spacing: 0.5px;
    background: rgba(245, 158, 11, 0.1);
    padding: 8px;
    border-radius: 6px;
    border: 1px solid rgba(245, 158, 11, 0.2);
    width: 100%;
    justify-content: center;
    text-align: center;
    animation: pulse 3s infinite;

    @keyframes pulse {
        0% { border-color: rgba(245, 158, 11, 0.2); }
        50% { border-color: rgba(245, 158, 11, 0.6); }
        100% { border-color: rgba(245, 158, 11, 0.2); }
    }
`

const DPI_Highlight_Container = styled.div`
    background: rgba(99, 102, 241, 0.05);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 8px;
    padding: 10px;
    margin-bottom: 5px;
    display: flex;
    flex-direction: column;
    gap: 5px;
`

const Quick_DPI_Btn = styled.button`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.7rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        border-color: rgba(255, 255, 255, 0.2);
    }
`

const Calc_Card = styled.div`
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    position: relative;
    width: 100%;
`

const Calc_Header = styled.div`
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    padding-bottom: 10px;
    margin-bottom: 5px;
`

const Calc_MainTitle = styled.div`
    font-size: 0.85rem;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

const Calc_Description = styled.div`
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.4);
    line-height: 1.4;
`

const Calc_Row = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    width: 100%;
    
`

const Calc_Divider = styled.div`
    height: 1px;
    background: rgba(255,255,255,0.05);
    width: 100%;
    margin: 3px 0;
`

const Calc_Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`

const Calc_InputLabel = styled.label`
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 600;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    text-transform: uppercase;

`

const Calc_ResultBox = styled.div`
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 6px;
    padding: 8px 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1;
    min-width: 100px;

`

const Calc_ResultLabel = styled.div`
    font-size: 0.6rem;
    color: rgba(99, 102, 241, 0.8);
    font-weight: 600;
    text-transform: uppercase;
    display:none;
`

const Calc_ResultValue = styled.div`
    font-size: 1rem;
    font-weight: 700;
    color: #fff;
`

const Calc_Button = styled.button`
    background: #6366f1;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 10px;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    width: 100%;

    &:hover:not(:disabled) {
        background: #5558e6;
        transform: translateY(-1px);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background: #444;
    }
`

// --- EXISTING COMPONENTS ---

const RawInput_Card = styled.div`
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.1);
    }
`

const Mouse_ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`

const Mouse_RawInputLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const Mouse_LabelSpan = styled.span`
    font-size: 0.8rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 0.5px;
`

const Mouse_HelpIcon = styled(HelpOutlineIcon)`
    font-size: 14px !important;
    opacity: 0.4;
    cursor: help;
`

const Mouse_Checkbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid ${({ checked }) => (checked ? '#6366f1' : '#666')};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background: ${({ checked }) => (checked ? '#6366f1' : 'transparent')};
    border-color: ${({ checked }) => (checked ? '#6366f1' : '#666')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        border-color: #6366f1;
    }
`

const Presets_Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
    margin-top: 60px;
`

const Presets_Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 15px;
    width: 100%;
`

const Presets_Header = styled.div`
    font-size: 0.75rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-left: 5px;
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
`

const Warning_Text = styled.span`
    color: #f59e0b;
    font-size: 0.65rem;
    display: flex;
    align-items: center;
    gap: 5px;
    background: rgba(245, 158, 11, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid rgba(245, 158, 11, 0.3);
`

const Preset_Card = styled.div`
    position: relative;
    background: rgba(15, 23, 42, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    height: 180px;
    justify-content: center;

    &:hover {
        transform: translateY(-5px);
        background: rgba(15, 23, 42, 0.6);
        border-color: rgba(99, 102, 241, 0.5);
        box-shadow: 0 10px 30px -10px rgba(99, 102, 241, 0.3);
        
        .preset-icon {
            color: #6366f1;
            transform: scale(1.1);
        }
    }
    
    ${({ active }) => active && `
        border-color: #6366f1;
        background: rgba(99, 102, 241, 0.1);
    `}
`

const Preset_IconWrapper = styled.div`
    font-size: 2.5rem;
    color: rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Preset_Title = styled.div`
    font-size: 0.9rem;
    font-weight: 700;
    color: #fff;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

const Preset_Desc = styled.div`
    font-size: 0.75rem;
    color: rgba(255,255,255,0.4);
    text-align: center;
    line-height: 1.4;
`

const Preset_OptionsOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.98);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    z-index: 10;
    animation: fadeIn 0.2s ease-out;
    padding: 15px;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`

const Option_Button = styled.button`
    background: rgba(99, 102, 241, 0.15);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #fff;
    padding: 8px 0;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    width: 100%;
    transition: all 0.2s;
    text-transform: uppercase;

    &:hover {
        background: #6366f1;
        border-color: #6366f1;
        transform: scale(1.02);
    }
`

const Close_Overlay_Btn = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: transparent;
    border: none;
    color: rgba(255,255,255,0.3);
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(255,255,255,0.1);
        color: #fff;
    }
`



export const MOUSE_METADATA = (t) => ({
    sensitivity: {
        label: t('SENSITIVITY'),
        desc: t('SENSITIVITY_DESC'),
        min: 0.1,
        max: 10, // Increased max for better flexibility
        step: 0.01,
        type: 'number',
    },
    zoom_sensitivity_ratio: {
        label: t('ZOOM_SENSITIVITY'),
        desc: t('ZOOM_SENSITIVITY_DESC'),
        min: 0.1,
        max: 2,
        step: 0.01,
        type: 'number',
    },
    m_rawinput: {
        label: t('RAW_INPUT'),
        desc: t('RAW_INPUT_DESC'),
        type: 'boolean',
    },

    m_yaw: {
        label: t('M_YAW'),
        desc: t('M_YAW_DESC'),
        min: 0.01,
        max: 0.05,
        step: 0.0001,
        type: 'number',
    },
    dpi: {
        label: t('DPI'),
        desc: t('DPI_DESC'),
        min: 100,
        max: 12000,
        step: 100,
        type: 'number',
    },
})

export default function MouseModule({ id, initialExpanded }) {
    const { t } = useLanguage()
    const { activeModules, configData, updateConfig, configIsLocked } = useConfig()
    const metadata = MOUSE_METADATA(t)
    const isActive = activeModules.includes('mouse')

    // Calculator State
    const [targetEdpi, setTargetEdpi] = useState(1000)
    const [calcResult, setCalcResult] = useState(null)
    const [activePresetId, setActivePresetId] = useState(null)


    const currentDpi = configData.mouse.dpi || 800

    useEffect(() => {
        if (targetEdpi && !isNaN(targetEdpi) && parseInt(targetEdpi) > 0) {
            const sens = (parseInt(targetEdpi) / currentDpi).toFixed(2)
            setCalcResult(sens)
        } else {
            setCalcResult(null)
        }
    }, [targetEdpi, currentDpi])

    const applyCalculatedSens = () => {
        if (calcResult && !configIsLocked) {
            handleChange('sensitivity', parseFloat(calcResult))
        }
    }

    const handleChange = (key, value) => {
        updateConfig('mouse', key, value)
    }

    const displayDesc = t('MOUSE_DESC')



    const edpiPresets = [400, 600, 800, 1000, 1200, 1400, 1600]

    const MOUSE_PRESETS = [
        {
            id: 'aim_max',
            title: t('MOUSE_PRESET_AIM_MAX_TITLE'),
            desc: t('MOUSE_PRESET_AIM_MAX_DESC'),
            edpi: 700,
            icon: <GpsFixedIcon style={{ fontSize: 'inherit' }} />
        },
        {
            id: 'agile',
            title: t('MOUSE_PRESET_AGILE_TITLE'),
            desc: t('MOUSE_PRESET_AGILE_DESC'),
            edpi: 1200,
            icon: <SpeedIcon style={{ fontSize: 'inherit' }} />
        },
        {
            id: 'pro_std',
            title: t('MOUSE_PRESET_PRO_STD_TITLE'),
            desc: t('MOUSE_PRESET_PRO_STD_DESC'),
            edpi: 900,
            icon: <StarIcon style={{ fontSize: 'inherit' }} />
        },
        {
            id: 'novice',
            title: t('MOUSE_PRESET_NOVICE_TITLE'),
            desc: t('MOUSE_PRESET_NOVICE_DESC'),
            edpi: 1600,
            icon: <SportsEsportsIcon style={{ fontSize: 'inherit' }} />
        }
    ]

    const handleApplyPreset = (edpi, aspectRatio) => {
        if (configIsLocked) return

        // 1. Calculate and set Sensitivity
        const newSens = (edpi / currentDpi).toFixed(3)
        updateConfig('mouse', 'sensitivity', parseFloat(newSens))

        // 2. Set m_yaw based on aspect ratio
        let newYaw = 0.022 // Default 16:9
        if (aspectRatio === '4:3') newYaw = 0.0165
        if (aspectRatio === '16:10') newYaw = 0.0198

        updateConfig('mouse', 'm_yaw', newYaw)
        setActivePresetId(null)
    }



    return (
        <ModuleContainer
            id={id}
            moduleKey="mouse"
            title={t('MOUSE_SENSITIVITY_TITLE')}
            icon={MouseIcon}
            description={displayDesc}
            initialExpanded={initialExpanded}
        >
            <Mouse_Controls isActive={isActive}>

                {/* HERO SECTION - FULL WIDTH */}
                <Mouse_HeroSection>
                    <Mouse_GridPattern />
                    <Mouse_GlowEffect />
                    <Mouse_HeroContent>
                        <Mouse_EDPI_Display>
                            <Mouse_EDPI_Label>{t('YOUR_EDPI')}</Mouse_EDPI_Label>
                            <Mouse_EDPI_Value>
                                {Math.round(
                                    configData.mouse.sensitivity *
                                    (configData.mouse.dpi || 800)
                                )}
                            </Mouse_EDPI_Value>
                        </Mouse_EDPI_Display>
                        <Mouse_Info_Side>
                            <Mouse_InfoText>{t('EDPI_DESC')}</Mouse_InfoText>
                            <Mouse_RecommendationBadge>
                                <span className="label">
                                    {t('EDPI_RECOMMENDED')}
                                </span>
                                <span className="range">700 - 1200</span>
                            </Mouse_RecommendationBadge>
                        </Mouse_Info_Side>
                    </Mouse_HeroContent>
                </Mouse_HeroSection>

                <Mouse_LayoutRow>
                    {/* LEFT COLUMN: SLIDERS & OPTIONS */}
                    <Mouse_MainColumn>
                        <Mouse_ControlGroup>
                            <ConfigSlider
                                label={metadata.sensitivity.label}
                                description={metadata.sensitivity.desc}
                                min={0.1}
                                max={5.0}
                                step={0.01}
                                value={configData.mouse.sensitivity}
                                onChange={(val) =>
                                    handleChange('sensitivity', val)
                                }
                                presets={[1, 1.6, 2, 2.5]}
                                showSliderInline={true}
                                showPresetsInline={true}
                                presetsPlacement="header"
                                disabled={configIsLocked}
                            />
                        </Mouse_ControlGroup>

                        <Mouse_ControlGroup>
                            <ConfigSlider
                                label={metadata.zoom_sensitivity_ratio.label}
                                description={
                                    metadata.zoom_sensitivity_ratio.desc
                                }
                                min={0.1}
                                max={2.0}
                                step={0.01}
                                value={configData.mouse.zoom_sensitivity_ratio}
                                onChange={(val) =>
                                    handleChange('zoom_sensitivity_ratio', val)
                                }
                                presets={[0.85, 1, 1.2]}
                                showPresetsInline={true}
                                presetsPlacement="header"
                                disabled={configIsLocked}
                            />
                        </Mouse_ControlGroup>

                        <Mouse_ControlGroup>
                            <ConfigSlider
                                label={metadata.m_yaw.label}
                                description={metadata.m_yaw.desc}
                                min={0.01}
                                max={0.05}
                                step={0.0001}
                                value={configData.mouse.m_yaw}
                                onChange={(val) => handleChange('m_yaw', val)}
                                presets={[
                                    { label: '16:9', val: 0.022 },
                                    { label: '4:3', val: 0.0165 },
                                    { label: '16:10', val: 0.0198 },
                                ]}
                                showPresetsInline={true}
                                presetsPlacement="header"
                                disabled={configIsLocked}
                            />
                        </Mouse_ControlGroup>

                        <RawInput_Card onClick={() => !configIsLocked && handleChange('m_rawinput', !configData.mouse.m_rawinput)}>
                            <Mouse_RawInputLabel>
                                <Mouse_LabelSpan>
                                    {metadata.m_rawinput.label}
                                </Mouse_LabelSpan>
                                <Tooltip
                                    title={metadata.m_rawinput.desc}
                                    placement="right"
                                    arrow
                                >
                                    <Mouse_HelpIcon />
                                </Tooltip>
                            </Mouse_RawInputLabel>
                            <Mouse_Checkbox
                                checked={configData.mouse.m_rawinput}
                                disabled={configIsLocked}
                            >
                                {configData.mouse.m_rawinput && (
                                    <CheckIcon
                                        style={{ fontSize: 14, color: 'white' }}
                                    />
                                )}
                            </Mouse_Checkbox>
                        </RawInput_Card>
                    </Mouse_MainColumn>

                    {/* RIGHT COLUMN: REFINED CALCULATOR */}
                    <Mouse_SidebarColumn>
                        <Calc_Card>
                            <Calc_Header>
                                <Calc_MainTitle>{t('CALC_TITLE')}</Calc_MainTitle>
                            </Calc_Header>

                            {/* DPI SECTION */}
                            <DPI_Highlight_Container>
                                <Calc_Section>
                                    <Calc_Row>
                                        <Calc_InputLabel>
                                            <span>{t('DPI')}</span>
                                            <BoltIcon style={{ fontSize: 12, color: '#f59e0b' }} />
                                        </Calc_InputLabel>
                                        <SmartValueInput
                                            value={configData.mouse.dpi || 800}
                                            onChange={(v) => handleChange('dpi', v)}
                                            min={100}
                                            max={12000}
                                            step={100}
                                            disabled={configIsLocked}
                                            label={t('DPI')}
                                        />
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            {[400, 800, 1200].map(val => (
                                                <Quick_DPI_Btn
                                                    key={val}
                                                    onClick={() => !configIsLocked && handleChange('dpi', val)}
                                                    title={`Set DPI to ${val}`}
                                                >
                                                    {val}
                                                </Quick_DPI_Btn>
                                            ))}
                                        </div>
                                    </Calc_Row>
                                    <Calc_Description>{t('CALC_DPI_DESC')}</Calc_Description>
                                </Calc_Section>
                            </DPI_Highlight_Container>


                            {/* TARGET EDPI SECTION */}
                            <Calc_Section>
                                <Calc_Row>
                                    <Calc_InputLabel>{t('TARGET_EDPI')}</Calc_InputLabel>
                                    <SmartValueInput
                                        value={targetEdpi}
                                        onChange={(v) => setTargetEdpi(v)}
                                        min={100}
                                        max={5000}
                                        step={50}
                                        presets={edpiPresets}
                                        disabled={configIsLocked}
                                        label={t('TARGET_EDPI')}
                                    />
                                </Calc_Row>
                                <Calc_Description>{t('CALC_TARGET_DESC')}</Calc_Description>
                            </Calc_Section>

                            <Calc_Divider />
                            <Calc_Row>
                                <Calc_ResultBox>
                                    <Calc_ResultLabel>{t('REQ_SENS')}</Calc_ResultLabel>
                                    <Calc_ResultValue>{calcResult || '-'}</Calc_ResultValue>
                                </Calc_ResultBox>
                                <Calc_Button
                                    disabled={!calcResult || configIsLocked}
                                    onClick={applyCalculatedSens}
                                >
                                    {t('APPLY')}
                                </Calc_Button>
                            </Calc_Row>
                        </Calc_Card>
                    </Mouse_SidebarColumn>
                </Mouse_LayoutRow>

                <Presets_Container>
                    <Presets_Header>
                        PRESETS
                        <Warning_Text>
                            {/* <WarningAmberIcon style={{ fontSize: 14 }} /> */}
                            <BoltIcon style={{ fontSize: 12, color: '#f59e0b' }} />

                            {t('MOUSE_PRESETS_DPI_LABEL')}
                            <div style={{ display: 'flex', gap: '4px', marginLeft: '5px' }}>
                                {[400, 800, 1200].map(val => (
                                    <Quick_DPI_Btn
                                        key={val}
                                        onClick={() => !configIsLocked && handleChange('dpi', val)}
                                        title={`Set DPI to ${val}`}
                                        style={{
                                            padding: '2px 6px',
                                            fontSize: '0.6rem',
                                            ...(currentDpi === val && {
                                                background: 'linear-gradient(135deg, #7c3aed, #db2777)',
                                                borderColor: '#a855f7',
                                                color: '#fff',
                                            })
                                        }}
                                    >
                                        {val}
                                    </Quick_DPI_Btn>
                                ))}
                            </div>
                        </Warning_Text>
                    </Presets_Header>
                    <Presets_Grid>
                        {MOUSE_PRESETS.map((preset) => (
                            <Preset_Card
                                key={preset.id}
                                onClick={() => !configIsLocked && setActivePresetId(preset.id)}
                                active={activePresetId === preset.id}
                            >
                                {activePresetId === preset.id ? (
                                    <Preset_OptionsOverlay onClick={(e) => e.stopPropagation()}>
                                        <Close_Overlay_Btn onClick={() => setActivePresetId(null)}>
                                            <CloseIcon style={{ fontSize: 16 }} />
                                        </Close_Overlay_Btn>
                                        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: '5px' }}>{t('SELECT_RATIO')}</span>
                                        <Option_Button onClick={() => handleApplyPreset(preset.edpi, '16:9')}>16:9 (Native)</Option_Button>
                                        <Option_Button onClick={() => handleApplyPreset(preset.edpi, '4:3')}>4:3 (Stretched)</Option_Button>
                                        <Option_Button onClick={() => handleApplyPreset(preset.edpi, '16:10')}>16:10</Option_Button>
                                    </Preset_OptionsOverlay>
                                ) : (
                                    <>
                                        <Preset_IconWrapper className="preset-icon">
                                            {preset.icon}
                                        </Preset_IconWrapper>
                                        <Preset_Title>{preset.title}</Preset_Title>
                                        <Preset_Desc>{preset.desc}</Preset_Desc>
                                    </>
                                )}
                            </Preset_Card>
                        ))}
                    </Presets_Grid>
                </Presets_Container>


            </Mouse_Controls>
        </ModuleContainer>
    )
}
